/**
 * bunbox — src/npm/resolve.ts
 * OWNED BY: resolver agent. Dependency-graph resolution: requested deps →
 * InstallPlan (flat, deduped by name@version, topologically ordered
 * parents-first).
 *
 * Semantics (npm-flavored, v1):
 *  - Runtime `dependencies` only. devDependencies are never followed;
 *    optionalDependencies are dropped; peerDependencies are NOT installed —
 *    each non-optional peer is recorded as a warning.
 *  - Range resolution = semver.maxSatisfying over the packument's versions;
 *    exact versions fast-path; bare dist-tags ("latest", "beta") supported.
 *  - Non-registry specs (git/github/http/file/workspace/npm-alias/paths)
 *    throw for directly-requested deps and warn+skip for transitive ones.
 *  - Cycle-safe: name@version dedupe bounds the BFS; the final ordering is a
 *    reverse DFS post-order, so parents precede children except across the
 *    back-edges of dependency cycles (unavoidable).
 *  - Deterministic: per-(name,spec) memo + alphabetical traversal order, so a
 *    fixed set of packuments always yields the identical plan.
 */

import semver from "semver";
import { fetchPackument } from "./registry.ts";
import type {
  InstallProgress,
  Packument,
  PackumentSource,
  PackumentVersion,
  ResolvedInstallPlan,
  ResolvedPackage,
} from "./types.ts";

export interface ResolveOptions {
  /** Packument source override (fixtures in tests). Default: fetchPackument. */
  getPackument?: PackumentSource;
  /** Streamed resolve-phase progress (`total` grows as the graph is discovered). */
  onProgress?: (p: InstallProgress) => void;
}

interface Task {
  name: string;
  spec: string;
  /** name@version of the requiring package, or null for direct requests. */
  parentKey: string | null;
}

const NON_REGISTRY_PREFIX = /^(git\+|git:|github:|gitlab:|bitbucket:|https?:|file:|link:|portal:|workspace:|npm:)/i;
const DIST_TAG_RE = /^[a-z0-9][a-z0-9._-]*$/i;

function isNonRegistrySpec(spec: string): boolean {
  if (NON_REGISTRY_PREFIX.test(spec)) return true;
  if (spec.startsWith(".") || spec.startsWith("/") || spec.startsWith("~/")) return true;
  // "user/repo" github shorthand (a bare dist-tag never contains a slash).
  if (spec.includes("/")) return true;
  return false;
}

/** Highest version satisfying `spec` (semver range, exact pin, or dist-tag). */
export function pickVersion(packument: Packument, spec: string): string | null {
  const trimmed = spec.trim();
  const range = trimmed === "" ? "*" : trimmed;

  // Exact pin fast path (also covers odd-but-published version strings).
  if (packument.versions[range]) return range;

  if (semver.validRange(range) !== null) {
    const best = semver.maxSatisfying(Object.keys(packument.versions), range);
    if (best !== null) return best;
    // "*"-style ranges exclude prereleases; fall back to the latest tag.
    const latest = packument["dist-tags"]?.["latest"];
    if ((range === "*" || range === "x") && latest && packument.versions[latest]) return latest;
    return null;
  }

  if (DIST_TAG_RE.test(range)) {
    const tagged = packument["dist-tags"]?.[range];
    if (tagged && packument.versions[tagged]) return tagged;
  }
  return null;
}

function describeRequirer(parentKey: string | null): string {
  return parentKey === null ? "(requested)" : `(required by ${parentKey})`;
}

export async function resolveInstallPlan(
  requestedDeps: Record<string, string>,
  options?: ResolveOptions,
): Promise<ResolvedInstallPlan> {
  const getPackument = options?.getPackument ?? fetchPackument;
  const onProgress = options?.onProgress;

  const packages = new Map<string, ResolvedPackage>();
  /** `${name} ${spec}` → resolved version. Determinism + parent→child edges. */
  const rangeMemo = new Map<string, string>();
  const warnings: string[] = [];
  const warned = new Set<string>();
  const rootKeys: string[] = [];

  const warn = (message: string): void => {
    if (warned.has(message)) return;
    warned.add(message);
    warnings.push(message);
  };

  const report = (detail: string, pendingCount: number): void => {
    onProgress?.({
      phase: "resolve",
      detail,
      done: packages.size,
      total: packages.size + pendingCount,
    });
  };

  const sortedEntries = (deps: Record<string, string>): [string, string][] =>
    Object.entries(deps).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  const resolveTask = async (task: Task): Promise<Task[]> => {
    const { name, spec, parentKey } = task;

    if (isNonRegistrySpec(spec)) {
      const message = `[npm] unsupported (non-registry) spec ${name}@${spec} ${describeRequirer(parentKey)}`;
      if (parentKey === null) throw new Error(message);
      warn(`${message} — skipped`);
      return [];
    }

    const packument = await getPackument(name);
    const memoKey = `${name} ${spec}`;
    let version = rangeMemo.get(memoKey);
    if (version === undefined) {
      const picked = pickVersion(packument, spec);
      if (picked === null) {
        throw new Error(
          `[npm] no version of ${name} satisfies "${spec}" ${describeRequirer(parentKey)}`,
        );
      }
      version = picked;
      rangeMemo.set(memoKey, version);
    }

    const key = `${name}@${version}`;
    if (parentKey === null && !rootKeys.includes(key)) rootKeys.push(key);
    if (packages.has(key)) return []; // dedupe: already resolved (also breaks cycles)

    const vm: PackumentVersion | undefined = packument.versions[version];
    if (vm === undefined || !vm.dist?.tarball) {
      throw new Error(`[npm] packument for ${key} has no dist.tarball`);
    }
    if (vm.deprecated) warn(`[npm] ${key} is deprecated: ${vm.deprecated}`);

    // Runtime deps only; entries mirrored in optionalDependencies are dropped.
    const optionalNames = new Set(Object.keys(vm.optionalDependencies ?? {}));
    const dependencies: Record<string, string> = {};
    const children: Task[] = [];
    for (const [depName, depSpec] of sortedEntries(vm.dependencies ?? {})) {
      if (optionalNames.has(depName)) continue;
      if (isNonRegistrySpec(depSpec)) {
        warn(`[npm] unsupported (non-registry) spec ${depName}@${depSpec} (required by ${key}) — skipped`);
        continue;
      }
      dependencies[depName] = depSpec;
      children.push({ name: depName, spec: depSpec, parentKey: key });
    }

    // Peers are never auto-installed; surface the non-optional ones.
    const peerMeta = vm.peerDependenciesMeta ?? {};
    for (const [peerName, peerRange] of sortedEntries(vm.peerDependencies ?? {})) {
      if (peerMeta[peerName]?.optional) continue;
      warn(`[npm] ${key} declares peer dependency ${peerName}@${peerRange} — not installed automatically`);
    }

    packages.set(key, {
      name,
      version,
      tarballUrl: vm.dist.tarball,
      ...(vm.dist.integrity !== undefined ? { integrity: vm.dist.integrity } : {}),
      dependencies,
    });
    report(key, children.length);
    return children;
  };

  // Breadth-first, one packument round-trip level at a time (fetches within a
  // level run in parallel; the registry client dedupes concurrent fetches).
  let frontier: Task[] = sortedEntries(requestedDeps).map(([name, spec]) => ({
    name,
    spec,
    parentKey: null,
  }));
  report("resolving", frontier.length);
  while (frontier.length > 0) {
    const childLists = await Promise.all(frontier.map(resolveTask));
    frontier = childLists.flat();
  }

  // Parents-first: reverse DFS post-order from the requested roots.
  const childKeysOf = (key: string): string[] => {
    const pkg = packages.get(key);
    if (!pkg) return [];
    const keys: string[] = [];
    for (const [depName, depSpec] of sortedEntries(pkg.dependencies)) {
      const version = rangeMemo.get(`${depName} ${depSpec}`);
      if (version === undefined) continue;
      const childKey = `${depName}@${version}`;
      if (packages.has(childKey)) keys.push(childKey);
    }
    return keys;
  };

  const postOrder: string[] = [];
  const visited = new Set<string>();
  const visit = (key: string): void => {
    if (visited.has(key)) return;
    visited.add(key);
    for (const child of childKeysOf(key)) visit(child);
    postOrder.push(key);
  };
  for (const root of rootKeys) visit(root);
  postOrder.reverse();

  const ordered = postOrder
    .map((key) => packages.get(key))
    .filter((pkg): pkg is ResolvedPackage => pkg !== undefined);

  report("resolved", 0);
  return { packages: ordered, requested: { ...requestedDeps }, warnings };
}
